/**
 * Speak an element name, using the BROWSER'S OWN voice.
 *
 * Instructor: "the periodic table needs a play button ... as we need to hear
 * CORRECT pronunciation as some are really hard."
 *
 * WHY THE WEB SPEECH API AND NOT THE TTS PIPELINE. The instructor's other
 * standing constraint is cost: "the read aloud button will burn lots of tokens
 * and we cant afford to keep pulling". `window.speechSynthesis` runs on the
 * device. It calls no provider, spends nothing per press, needs no network,
 * and works in an offline lesson (BP 20) — so a student can tap "Molybdenum"
 * forty times while they get it right and it costs exactly nothing. Routing
 * this through the paid TTS path would have reintroduced the problem we were
 * just asked to remove.
 *
 * The honest trade: the voice is whatever the device provides, so quality
 * varies between an iPad and a Windows laptop, and a few browsers ship none at
 * all. That is why `canSpeak()` exists — the control is HIDDEN where speech is
 * unavailable rather than present and silent.
 *
 * Not verified on a real iPad. Safari is the likeliest place for this to
 * behave differently (it populates voices asynchronously), which is handled
 * below but is reasoned, not observed.
 */

/** Is speech synthesis usable in this browser right now? */
export function canSpeak(): boolean {
  try {
    return typeof window !== 'undefined'
      && 'speechSynthesis' in window
      && typeof window.SpeechSynthesisUtterance === 'function';
  } catch {
    // A privacy-hardened browser can throw on the property access itself.
    return false;
  }
}

/**
 * Pick an English voice if one is offered.
 *
 * Element names are Latin/Greek-derived and a non-English default voice
 * mangles them — the whole point here is a learner hearing the pronunciation
 * their instructor will use. Safari populates this list asynchronously, so an
 * empty list is normal on the first call and must not be treated as failure.
 */
function englishVoice(): SpeechSynthesisVoice | null {
  try {
    const voices = window.speechSynthesis.getVoices() || [];
    if (voices.length === 0) return null;
    return (
      voices.find((v) => /^en[-_]/i.test(v.lang) && v.localService)
      || voices.find((v) => /^en[-_]/i.test(v.lang))
      || null
    );
  } catch {
    return null;
  }
}

/**
 * Say `name`. Cancels anything already speaking, so a learner tapping through
 * several elements hears the one they just tapped rather than a queue.
 * Returns false when nothing could be said.
 */
export function speakElementName(name: string): boolean {
  if (!canSpeak() || !name) return false;
  try {
    window.speechSynthesis.cancel();
    const utterance = new window.SpeechSynthesisUtterance(name);
    utterance.lang = 'en-US';
    // Slightly under normal pace: these are long unfamiliar words and the
    // point is to be able to make out the syllables, not to be quick.
    utterance.rate = 0.9;
    const voice = englishVoice();
    if (voice) utterance.voice = voice;
    window.speechSynthesis.speak(utterance);
    return true;
  } catch {
    return false;
  }
}

/** Stop whatever is being said. */
export function stopSpeaking(): void {
  try {
    if (canSpeak()) window.speechSynthesis.cancel();
  } catch { /* nothing to stop */ }
}
